<template>
  <div class="all-animals-ranking">
    <h3 class="title">匹配度排名</h3>
    <p class="subtitle">看看你与其他动物原型的相似度（前3名）</p>
    
    <div class="ranking-list">
      <div 
        v-for="(item, index) in topRanking" 
        :key="item.name"
        class="ranking-item"
        :class="{ 
          'rank-1': index === 0, 
          'rank-2': index === 1, 
          'rank-3': index === 2 
        }"
      >
        <!-- 排名 -->
        <div class="rank-badge">
          <span class="medal">{{ getMedal(index) }}</span>
        </div>

        <!-- 动物信息 -->
        <div class="animal-info">
          <span class="animal-emoji">{{ item.animal.emoji }}</span>
          <div class="animal-details">
            <div class="animal-name">{{ item.animal.name }}</div>
            <div class="animal-tags">
              <span 
                v-for="tag in item.animal.tags.slice(0, 2)" 
                :key="tag"
                class="tag"
              >
                {{ tag }}
              </span>
            </div>
          </div>
        </div>

        <!-- 匹配度 -->
        <div class="similarity-section">
          <div class="similarity-bar-container">
            <div 
              class="similarity-bar" 
              :style="{ 
                width: (getDisplaySimilarity(item, index) * 100) + '%',
                backgroundColor: item.animal.color
              }"
            >
              <span class="similarity-text">{{ (getDisplaySimilarity(item, index) * 100).toFixed(1) }}%</span>
            </div>
          </div>
        </div>

        <!-- 查看详情按钮 -->
        <button 
          class="view-detail-btn"
          @click="$emit('viewAnimal', item.name)"
          :style="{ 
            borderColor: item.animal.color,
            color: item.animal.color
          }"
        >
          查看
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

/* eslint-disable no-undef */
const props = defineProps({
  ranking: {
    type: Array,
    required: true
  }
})

defineEmits(['viewAnimal'])
/* eslint-enable no-undef */

// 只取前3名
const topRanking = computed(() => {
  return props.ranking.slice(0, 3)
})

// 算法参数配置
const GAP_THRESHOLD = 0.05 // 差距阈值：5%
const SECOND_PLACE_MULTIPLIER = 2.0 // 第2名放大系数
const SECOND_PLACE_MIN_GAP = 0.10 // 第2名最小差距：5%
const THIRD_PLACE_MULTIPLIER = 2.0 // 第3名放大系数
const THIRD_PLACE_MIN_GAP = 0.05 // 第3名最小差距：4%

// 计算调整后的显示相似度值
const adjustedSimilarities = computed(() => {
  const top3 = topRanking.value
  
  if (top3.length === 0) return []
  
  const result = []
  
  // 第1名：保持原值
  result[0] = Math.max(0, Math.min(1, top3[0].similarity))
  
  // 第2名：如果与第1名差距小，则调整
  if (top3.length >= 2) {
    const gap2 = result[0] - top3[1].similarity
    
    if (gap2 < GAP_THRESHOLD && gap2 > 0) {
      // 差距小于阈值，放大差距
      const adjustedGap2 = Math.max(gap2 * SECOND_PLACE_MULTIPLIER, SECOND_PLACE_MIN_GAP)
      result[1] = Math.max(0, Math.min(1, result[0] - adjustedGap2))
      
      // 确保第2名不会超过第1名
      if (result[1] >= result[0]) {
        result[1] = Math.max(0, result[0] - SECOND_PLACE_MIN_GAP)
      }
    } else {
      // 差距足够大，保持原值
      result[1] = Math.max(0, Math.min(1, top3[1].similarity))
    }
  }
  
  // 第3名：如果与第2名差距小，则调整
  if (top3.length >= 3) {
    // 使用调整后的第2名来计算差距（如果第2名被调整了，就用调整后的值；否则用原始值）
    const secondPlaceValue = result[1] !== undefined ? result[1] : top3[1].similarity
    const originalThirdPlace = top3[2].similarity
    const gap3 = secondPlaceValue - originalThirdPlace
    
    // 如果第3名原始值比调整后的第2名还高，强制降低第3名
    if (originalThirdPlace >= secondPlaceValue) {
      // 强制降低，确保第3名低于第2名
      result[2] = Math.max(0, Math.min(1, secondPlaceValue - THIRD_PLACE_MIN_GAP))
    } else if (gap3 < GAP_THRESHOLD && gap3 > 0) {
      // 差距小于阈值，放大差距
      const adjustedGap3 = Math.max(gap3 * THIRD_PLACE_MULTIPLIER, THIRD_PLACE_MIN_GAP)
      result[2] = Math.max(0, Math.min(1, secondPlaceValue - adjustedGap3))
    } else {
      // 差距足够大，保持原值，但确保不超过第2名
      result[2] = Math.max(0, Math.min(1, originalThirdPlace))
    }
    
    // 最终安全检查：确保第3名严格小于第2名（防止任何边界情况）
    if (result[2] >= secondPlaceValue) {
      result[2] = Math.max(0, secondPlaceValue - THIRD_PLACE_MIN_GAP)
    }
  }
  
  return result
})

// 获取显示用的相似度值
const getDisplaySimilarity = (item, index) => {
  // 确保索引有效
  if (index < 0 || index >= adjustedSimilarities.value.length) {
    return Math.max(0, Math.min(1, item.similarity))
  }
  
  return adjustedSimilarities.value[index]
}

const getMedal = (index) => {
  const medals = ['🥇', '🥈', '🥉']
  return medals[index]
}
</script>

<style scoped>
.all-animals-ranking {
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
  text-align: center;
}

.subtitle {
  font-size: 0.95rem;
  color: #718096;
  text-align: center;
  margin: 0 0 2rem 0;
}

.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ranking-item {
  display: grid;
  grid-template-columns: 60px 1fr 2fr auto;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 12px;
  background: #f7fafc;
  transition: all 0.3s ease;
  position: relative;
}

.ranking-item:hover {
  background: #edf2f7;
  transform: translateX(4px);
}

.ranking-item.rank-1 {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #fbbf24;
}

.ranking-item.rank-2 {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  border: 2px solid #818cf8;
}

.ranking-item.rank-3 {
  background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
  border: 2px solid #fb923c;
}

.rank-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  font-weight: bold;
}

.medal {
  font-size: 2rem;
}

.rank-number {
  font-size: 1.25rem;
  color: #4a5568;
  background: white;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.animal-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.animal-emoji {
  font-size: 2.5rem;
  flex-shrink: 0;
}

.animal-details {
  flex: 1;
  min-width: 0;
}

.animal-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 0.25rem;
  word-break: break-word;
  line-height: 1.4;
}

.animal-tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.tag {
  padding: 0.25rem 0.5rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  font-size: 0.75rem;
  color: #4a5568;
  white-space: nowrap;
}

.similarity-section {
  flex: 1;
}

.similarity-bar-container {
  width: 100%;
  height: 32px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  overflow: hidden;
  position: relative;
}

.similarity-bar {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 0.75rem;
  border-radius: 16px;
  transition: width 0.8s ease;
  min-width: 60px;
}

.similarity-text {
  font-size: 0.9rem;
  font-weight: bold;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.view-detail-btn {
  padding: 0.5rem 1rem;
  border: 2px solid;
  border-radius: 8px;
  background: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.view-detail-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

@media (max-width: 1024px) {
  .ranking-item {
    grid-template-columns: 50px 1fr 1.5fr auto;
    gap: 0.75rem;
  }

  .animal-emoji {
    font-size: 2rem;
  }
}

@media (max-width: 768px) {
  .all-animals-ranking {
    padding: 1.5rem;
  }

  .title {
    font-size: 1.25rem;
  }

  .subtitle {
    font-size: 0.875rem;
    margin-bottom: 1.5rem;
  }

  .ranking-item {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
    align-items: stretch;
  }

  .rank-badge {
    position: absolute;
    top: 0.75rem;
    left: 0.75rem;
    width: 36px;
    height: 36px;
  }

  .medal {
    font-size: 1.5rem;
  }

  .rank-number {
    font-size: 1rem;
    width: 32px;
    height: 32px;
  }

  .animal-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding-left: 2.5rem;
    min-width: 0;
  }

  .animal-emoji {
    font-size: 2rem;
    flex-shrink: 0;
  }

  .animal-details {
    flex: 1;
    min-width: 0;
  }

  .animal-name {
    font-size: 0.95rem;
    white-space: normal;
    word-break: break-word;
  }

  .animal-tags {
    margin-top: 0.25rem;
  }

  .tag {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }

  .similarity-section {
    width: 100%;
    margin-top: 0.25rem;
  }

  .similarity-bar-container {
    height: 28px;
  }

  .similarity-bar {
    min-width: 50px;
    padding-right: 0.5rem;
  }

  .similarity-text {
    font-size: 0.8rem;
  }

  .view-detail-btn {
    width: 100%;
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }

  .ranking-item.rank-1,
  .ranking-item.rank-2,
  .ranking-item.rank-3 {
    position: relative;
  }
}

@media (max-width: 480px) {
  .all-animals-ranking {
    padding: 1rem;
  }

  .title {
    font-size: 1.125rem;
  }

  .subtitle {
    font-size: 0.8rem;
    margin-bottom: 1rem;
  }

  .ranking-list {
    gap: 0.75rem;
  }

  .ranking-item {
    padding: 0.75rem;
    gap: 0.5rem;
  }

  .rank-badge {
    top: 0.5rem;
    left: 0.5rem;
    width: 32px;
    height: 32px;
  }

  .medal {
    font-size: 1.25rem;
  }

  .rank-number {
    font-size: 0.875rem;
    width: 28px;
    height: 28px;
  }

  .animal-info {
    padding-left: 2rem;
    gap: 0.5rem;
  }

  .animal-emoji {
    font-size: 1.75rem;
  }

  .animal-name {
    font-size: 0.875rem;
  }

  .tag {
    font-size: 0.65rem;
    padding: 0.15rem 0.35rem;
  }

  .similarity-bar-container {
    height: 24px;
  }

  .similarity-bar {
    min-width: 45px;
    padding-right: 0.4rem;
  }

  .similarity-text {
    font-size: 0.75rem;
  }

  .view-detail-btn {
    padding: 0.5rem 0.875rem;
    font-size: 0.8rem;
  }
}
</style>

